import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { ChevronLeft, MapPin } from 'lucide-react'
import toast from 'react-hot-toast'
import { TransmissionView, DEFAULT_THEME } from '@shared/TransmissionView'
import { getAreaDetail, likeExhibitionPost } from '../api/transmissionService'
import { ROUTES } from '@app/router/routes'
import PageHeader from '@shared/layouts/PageHeader'
import BottomNav from '@shared/layouts/BottomNav'
import { getUserInfo } from '@shared/api/client'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { isFeatureAccessible, FEATURE_FLAGS } from '@shared/config/featureFlags'

const DEFAULT_SETTINGS = {
  intro: { gradient: true },
  modelImagePosition: 'left',
  modelTitleStyle: 'left',
  modelBg: 'color',
  modelSubtitleAlign: 'left',
  artifactImagePosition: 'right',
  artifactTitleStyle: 'left',
  artifactBg: 'color',
  silhouetteVariant: 1,
}

export default function TransmissionAreaPage() {
  const { areaId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const mapRestoreFrom = location.state?.mapRestore?.from ?? null
  const mapRestoreActiveZoneId = location.state?.mapRestore?.activeZoneId ?? null
  const [searchParams] = useSearchParams()
  const templateId = searchParams.get('t') ?? (areaId != null ? parseInt(areaId, 10) + 1 : null)
  const { t, currentLanguage } = useLanguage()

  const [user, setUser] = useState(() => getUserInfo())
  const isWishTreeEnabled = isFeatureAccessible(FEATURE_FLAGS.WISH_TREE, !!user)
  const isEn = currentLanguage.code === 'en'
  const pillFontSize = isEn ? 22 : 32
  const pillSpacing = isEn ? 3 : 7
  const [blocks, setBlocks] = useState([])
  const [theme, setTheme] = useState(DEFAULT_THEME)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [postId, setPostId] = useState(null)
  const [likeCount, setLikeCount] = useState(0)
  const [isLiked, setIsLiked] = useState(false)
  const [likeLoading, setLikeLoading] = useState(false)

  const [locatingMap, setLocatingMap] = useState(false)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    const onAuthChange = () => setUser(getUserInfo())
    window.addEventListener('auth-change', onAuthChange)
    return () => window.removeEventListener('auth-change', onAuthChange)
  }, [])

  useEffect(() => {
    if (!areaId) return
    setIsLoading(true)
    getAreaDetail(areaId, templateId)
      .then(res => {
        const item = res.items?.[0]
        if (!item) {
          setNotFound(true)
          return
        }

        const raw = item.template_detail ?? item.detail ?? {}
        const detail = typeof raw === 'string' ? JSON.parse(raw) : raw

        setBlocks(detail.blocks ?? [])
        if (detail.theme) setTheme(detail.theme)
        if (detail.settings) setSettings(detail.settings)

        setPostId(item.post_id ?? null)
        setLikeCount(item.like_count ?? 0)
        setIsLiked(item.has_liked ?? item.is_liked ?? false)
      })
      .catch((e) => {
        // console.log('error', e)
        setNotFound(true)
      })
      .finally(() => setIsLoading(false))
  }, [areaId, templateId, currentLanguage])

  const handleLike = async () => {
    if (likeLoading || isLiked || !postId) return

    setIsLiked(true)
    setLikeCount(c => c + 1)
    setLikeLoading(true)

    try {
      const res = await likeExhibitionPost(postId)
      setLikeCount(res.like_count ?? likeCount + 1)
    } catch (err) {
      setIsLiked(false)
      setLikeCount(c => c - 1)
      toast.error(err?.message || err?.data?.reason || t('transmission-detail.likeFailed'))
    } finally {
      setLikeLoading(false)
    }
  }

  // 返回鍵預設瀏覽器後退；只有從互動地圖的展區熱區進來時才需要指定回哪一層地圖
  // （這個入口不一定跟前一個 history entry 的地圖狀態一致，單純 navigate(-1) 無法保證回到正確的層級）
  const handleBack = () => {
    if (mapRestoreFrom === 'hongfa-detail') {
      // 重新打開全螢幕層 + ZoneDetailMap，不帶 activeZone/selectedTableId，維持在 area_2.2.jpg 遠景畫面
      navigate(ROUTES.TRANSMISSION, { state: { mapRestore: { isFullscreen: true, activeZoneId: 'zone2', zone2: {}, noTargetMarker: true } } })
      return
    }
    if (mapRestoreFrom === 'fullscreen' && mapRestoreActiveZoneId) {
      navigate(ROUTES.TRANSMISSION, { state: { mapRestore: { isFullscreen: true, activeZoneId: mapRestoreActiveZoneId, noTargetMarker: true } } })
      return
    }
    if (mapRestoreFrom === 'static' && mapRestoreActiveZoneId) {
      navigate(ROUTES.TRANSMISSION, { state: { mapRestore: { isFullscreen: false, activeZoneId: mapRestoreActiveZoneId, noTargetMarker: true } } })
      return
    }
    navigate(-1)
  }

  const handleLocateOnMap = () => {
    if (locatingMap || areaId == null) return
    setLocatingMap(true)
    try {
      const zoneMap = ['entrance', 'zone1', 'zone2', 'zone3', 'zone4']
      const activeZoneId = zoneMap[parseInt(areaId, 10)]
      if (!activeZoneId) {
        toast.error(t('transmission-detail.locateNotFound'))
        return
      }
      // 所有展區統一回全螢幕層並顯示紅點定位
      navigate(ROUTES.TRANSMISSION, {
        state: {
          mapRestore: {
            isFullscreen: true,
            activeZoneId,
          },
        },
      })
    } catch (err) {
      toast.error(err?.message || t('transmission-detail.locateFailed'))
    } finally {
      setLocatingMap(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="material-symbols-outlined animate-spin text-4xl text-[var(--color-primary)]">
          progress_activity
        </span>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white relative">
        <PageHeader user={user} showHome />
        <div className="flex flex-col items-center justify-center gap-4 pt-32">
          <p className="text-lg" style={{ color: 'var(--color-text-sub)' }}>{t('transmission-detail.noContent')}</p>
          <button
            onClick={() => navigate(ROUTES.TRANSMISSION)}
            className="px-6 py-2 rounded-full text-sm font-medium"
            style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
          >
            {t('transmission-detail.backToList')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="transmission-area-page min-h-screen bg-white relative pb-20 md:pb-0 animate-zoomIn">
      <PageHeader user={user} showHome />

      {/* Back Button */}
      <div className="fixed top-[80px] md:top-[96px] left-4 md:left-8 z-30">
        <button
          onClick={handleBack}
          className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer"
          aria-label={t('transmission-detail.backAria')}
        >
          <ChevronLeft size={24} className="mr-0.5" />
        </button>
      </div>

      {/* Locate on Map Button */}
      <div className="fixed top-[80px] md:top-[96px] right-4 md:right-8 z-30">
        <button
          onClick={handleLocateOnMap}
          disabled={locatingMap}
          className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label={t('transmission-detail.locateAria')}
        >
          <MapPin size={20} />
        </button>
      </div>

      {/* Fixed Like Button */}
      {postId && (
        <div className="fixed right-4 md:right-6 bottom-24 z-50">
          <button
            onClick={handleLike}
            disabled={likeLoading || isLiked}
            className={`flex flex-col items-center gap-1 w-14 h-14 rounded-full border-2 shadow-xl transition-all duration-200 select-none
              ${isLiked
                ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white cursor-default'
                : 'bg-white/90 border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white cursor-pointer'
              }
              ${likeLoading ? 'opacity-60 cursor-not-allowed' : ''}
            `}
            aria-label={t('transmission-detail.likeAria')}
          >
            <span
              className="material-symbols-outlined text-xl leading-none mt-2"
              style={{ fontVariationSettings: isLiked ? "'FILL' 1" : "'FILL' 0" }}
            >
              favorite
            </span>
            <span className="text-xs font-bold leading-none mb-1">{likeCount}</span>
          </button>
        </div>
      )}

      <div className="pt-[70px]">
        <TransmissionView
          blocks={blocks}
          theme={theme}
          settings={settings}
          isMobile={isMobile}
          areaId={areaId}
        />

        {/* 展區四：願心樹發願入口 */}
        {user && String(areaId) === '4' && (
          <a
            href={isWishTreeEnabled ? ROUTES.WISH_TREE : undefined}
            aria-disabled={!isWishTreeEnabled}
            tabIndex={!isWishTreeEnabled ? -1 : 0}
            className="block relative mx-4 my-6 rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.15)]"
            style={{
              opacity: isWishTreeEnabled ? 1 : 0.45,
              pointerEvents: isWishTreeEnabled ? 'auto' : 'none',
              cursor: isWishTreeEnabled ? 'pointer' : 'not-allowed',
              background: 'linear-gradient(180deg, #F3E9D8 0%, #E5D3B8 100%)',
            }}
          >
            <div
              className="flex justify-center pl-14 pt-4"
            >
              <span
                style={{
                  fontSize: pillFontSize,
                  fontFamily: 'Noto Serif TC',
                  fontWeight: 700,
                  letterSpacing: pillSpacing,
                  color: '#BC313D',
                  // WebkitTextStroke: '5px black',
                  paintOrder: 'stroke fill',
                  lineHeight: 1.2,
                }}
              >
                {t('nav.goToWishTree')}
              </span>
            </div>
            <img
              src="/image/wishtree/tree_s.png"
              alt={t('nav.wishTreeShort')}
              className="w-full h-auto object-contain"
              draggable={false}
            />
          </a>
        )}
      </div>

      <BottomNav className="md:hidden" />
    </div>
  )
}
